import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium',
    'transition-colors select-none',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
    // Un bouton desactive doit cesser d'avoir l'air actif.
    //
    //  L'opacite seule ne suffit pas sur une couleur de marque : sur la page
    //  d'ontologie, « Essayer » (secondaire, gris) et « Enregistrer la regle »
    //  (primaire, bleu) etaient desactives par la meme condition, et le second
    //  paraissait cliquable a 50 % d'opacite. On lisait donc un ecran qui
    //  semblait interdire ce qu'il recommandait et autoriser ce qu'il
    //  deconseillait.
    'disabled:pointer-events-none disabled:opacity-60',
    'disabled:bg-surface-muted disabled:text-muted-foreground disabled:border-border',
    'disabled:shadow-none disabled:[&_svg]:opacity-70',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ),
  {
    variants: {
      variant: {
        primary: 'bg-brand text-brand-foreground hover:bg-brand-700 dark:hover:bg-brand-400',
        secondary:
          'border border-border-strong bg-surface text-foreground hover:bg-surface-muted',
        ghost: 'text-muted-foreground hover:bg-surface-muted hover:text-foreground',
        danger: 'bg-danger text-white hover:brightness-90 dark:text-[#2a0508]',
        'danger-outline':
          'border border-danger/40 bg-transparent text-danger hover:bg-danger-subtle',
        link: 'text-brand underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 px-2.5 text-xs [&_svg]:size-3.5',
        md: 'h-9 px-3.5 text-sm [&_svg]:size-4',
        lg: 'h-10 px-4 text-sm [&_svg]:size-4',
        icon: 'size-9 [&_svg]:size-4',
        'icon-sm': 'size-8 [&_svg]:size-3.5',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Shows a spinner and blocks interaction. Use for in-flight mutations. */
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild = false, loading = false, children, disabled, ...props },
  ref,
) {
  const classes = cn(buttonVariants({ variant, size }), className);

  if (asChild) {
    // Radix Slot is convenient but throws at runtime when a composed primitive
    // forwards multiple children. Button-as-link only needs to pass its visual
    // and interaction props to one explicit child, which React can do safely.
    const child = React.Children.only(children) as React.ReactElement<{
      className?: string;
      children?: React.ReactNode;
    }>;
    return React.cloneElement(child, {
      ...props,
      ref,
      className: cn(classes, child.props.className),
      'aria-busy': loading || undefined,
      'aria-disabled': disabled || loading || undefined,
      children: loading ? <Loader2 className="animate-spin" aria-hidden /> : child.props.children,
    } as React.Attributes & Record<string, unknown>);
  }

  return (
    <button
      ref={ref}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Loader2 className="animate-spin" aria-hidden /> : null}
      {children}
    </button>
  );
});

export { buttonVariants };
